import APIAxios, {APIRoutes} from '@api/axios.api';
import {GridSortItem} from '@mui/x-data-grid-pro';
import {UIEntitiesRef, upsertEntities, upsertEntitiesById} from '@ngneat/elf-entities';
import {setPage, updatePaginationData} from '@ngneat/elf-pagination';
import {GlobalStatus} from '@store/common/globalStatus.enum';
import {Pagination} from '@store/common/pagination.model';
import {TableFilter} from '@store/common/tableFilter.model';
import {Report, ReportsFilters, ReportsPagination, ReportStatusEnum} from '@store/reports/reports.model';
import {getReportsDataSource} from '@store/reports/reports.requests';
import {reportStore} from '@store/reports/reports.store';
import SnackError from '@utils/error.utils';
import fileDownload from 'js-file-download';
import {extractFilenameFromResponse} from '@utils/response.utils';
import {filesService} from '@store/files';
import {catchError, from, map, Observable, switchMap, tap} from 'rxjs';
import {AxiosError, AxiosResponse} from 'axios';
import dayjs from 'dayjs';

export class ReportsService {

  store = reportStore;

  resetStore = () => this.store.reset();

  setSort = (sort: GridSortItem) => this.store.update((state) => ({...state, sort}));
  setStatus = (status?: GlobalStatus) => this.store.update((state) => ({...state, status}));

  setPagination = (name: string) => (value: number) => this.store.update((state) => ({
    ...state,
    tablePagination: {
      ...state.tablePagination,
      [name]: value,
    },
  }));

  resetFilters = (clientId?: string) => this.store.update((state) => ({
    ...state,
    filters: {search: '', tableFilters: ReportsFilters.initTableFilters(clientId)},
  }));

  setFilters = (name: string) => (value: any) => this.store.update((state) => ({
    ...state,
    filters: {
      ...state.filters,
      [name]: value,
    },
  }));

  downloadReport = (createReportDto: any, filterDates?: ReportsFilters) => {
    const params = {
      from: createReportDto.from ?? filterDates?.from,
      to: createReportDto.to ?? filterDates?.to,
      clientOrganisationId: createReportDto.clientOrganisationId,
      clientOrganisationUnitIds: createReportDto.clientOrganisationUnitIds,
      supplierOrganisationUnitIds: createReportDto.supplierOrganisationUnitIds,
      wasteFamilyIds: createReportDto.wasteFamilyIds,
      wasteIds: createReportDto.wasteIds,
    };
    return from(APIAxios({
      ...APIRoutes.GETDownloadReport(),
      params: params

    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        tap((response: AxiosResponse<Blob>) => {
          const filename = extractFilenameFromResponse(response);
          fileDownload(response.data, filename || 'invoices.xlsx');
        }),
        switchMap((data) => this.getReports().pipe(map(() => data))),
      );
  };

  createReport = (createReportDto: any, filterDates?: ReportsFilters): Observable<Report> => {

    return from(APIAxios({
      ...APIRoutes.CREATEReport(),
      data: {
        clientOrganisation: createReportDto.clientOrganisationId,
        startDate: createReportDto.from ?? filterDates?.from,
        endDate: createReportDto.to ?? filterDates?.to,
      },

    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<Report>) => {
          return response.data;
        }));
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  generateReport = (createReportDto: any, filterDates?: ReportsFilters, status?: GlobalStatus): Observable<Blob> => {
    const params = {
      from: dayjs(createReportDto.from).hour(10).toDate(),
      to: dayjs(createReportDto.to).hour(10).toDate(),
      clientOrganisationId: createReportDto?.clientOrganisationId
    };
    //TODO CHECK STATUS
    // if (status) {
    //     params['status'] = status
    // }
    return from(APIAxios({
      ...APIRoutes.GETGenerateReport(),
      params: params

    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        tap((response: AxiosResponse<Blob>) => {
          const filename = extractFilenameFromResponse(response);
          filesService.downloadDocument(response.data, filename || 'report.xlsx');

        }),
        map((response: AxiosResponse<Blob>) => {
          return response.data;
        }),
        switchMap((data) => this.getReports().pipe(map(() => data))),
      );
  };

  getReports = () => {
    const {status, filters: {search, tableFilters}, sort, tablePagination} = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);
    return from(APIAxios({
      ...APIRoutes.GETReports(),
      params: {
        ...requestFilters,
        searchString: search || undefined,
        status: status ? status : ReportStatusEnum.CREATED,
        page: tablePagination.page,
        limit: tablePagination.limit,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ReportsPagination>) => {
          return response.data;
        }),
        tap((reportsPagination) => {
          const paginationData = Pagination.toPaginationData(reportsPagination, tablePagination.page);

          return this.store.update(
            upsertEntities(reportsPagination.data),
            upsertEntitiesById(reportsPagination.data.map((suppliersUnit) => suppliersUnit.id), {
              updater: (reportsUi) => reportsUi,
              creator: (reportId) => ({id: reportId, selected: false}),
              ref: UIEntitiesRef,
            }),
            updatePaginationData(paginationData),
            setPage(paginationData.currentPage, reportsPagination.data.map((report) => report.id)),
            getReportsDataSource.setSuccess(),
          );
        }),
        getReportsDataSource.trackRequestStatus(),
      );
  };

  updateReport = (reportId: string | number, report: Report): Observable<Report> => {
    return from(APIAxios({
      ...APIRoutes.PATCHReport(reportId),
      data: {
        startDate: report.startDate,
        endDate: report.endDate,
        status: report.status,
        clientOrganisation: report.organisation?.id,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<Report>) => {
          return response.data;
        }),
        switchMap(() => this.getReports().pipe(map(() => report))),
      );
  };

  getSelectedReport = (reportId: number | string): Observable<Report> => {
    return from(APIAxios(APIRoutes.GETReportById(reportId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<Report>) => {
          return response.data;
        }),
      );
  };

  deleteReport = (reportId: number | string): Observable<ReportsPagination> => {
    return from(APIAxios(APIRoutes.DELETEReport(reportId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getReports()),
      );
  };

  activeOrArchiveReport = (reportId: number | string, status: ReportStatusEnum): Observable<ReportsPagination> => {
    return from(APIAxios({
      ...APIRoutes.PATCHReportArchive(reportId),
      data: {
        status: status
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getReports()),
      );
  };

}

export const reportsService = new ReportsService();
